import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import cN from 'classnames';
import { store, RootState } from '@Redux/search/store';
import { I_FilterItem, I_NormGroup } from '@Redux/search/interfaces';
import { setPageType } from '@Redux/common/Page/actions';
import { setTrigger } from '@Redux/search/actions';
import i18n from '@utils/i18n';
import domainbyEnv from '@utils/domainByEnv';
import { createSearchPanel } from '@utils/createSearchPanel';
import SearchPanelBlock from '@components/Common/SearchPanelBlock';
import SlidePage from '@components/Common/SlidePage';
import * as Icons from '@components/Common/Icons';
import BreadCrumb from '@components/Common/BreadCrumb';
import TagFilters from '@components/SearchPage/TagFilters';
import Conditions from '@components/SearchPage/Conditions';
import Sorters from '@components/SearchPage/Sorters';
import Filters from '@components/SearchPage/Filters';
import ResultCards from '@components/SearchPage/ResultCards';
import '@magaele/cd_gpsm';
import '@magaele/cd_gpsm/css.css';
import '@magaele/ic-ln/css.css';
import 'normalize.css';
import './styles.scss';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

dayjs.extend(duration);

declare global {
    interface Window {
        dataLayer: any;
    }
}
i18n();

const urlParams = new URLSearchParams(window.location.search);
const ArriveID = urlParams.get('ArriveID');
const Keywords = urlParams.get('Keywords');
const GroupID = urlParams.get('GroupID');
const TravelType = urlParams.get('TravelType');

const SearchPanelBlockM = () => {
    const [isOpenPanel, setIsOpenPanel] = useState<boolean>(false);
    const [defaultArrive, setDefaultArrive] = useState<string>('');
    const [isScroll, setIsScroll] = useState<boolean>(false);

    useScrollPosition(({ currPos }) => {
        const isSrollPage = -currPos.y > 150;
        isSrollPage ? setIsScroll(true) : setIsScroll(false);
    });

    useEffect(() => {
        createSearchPanel({
            device: 'M',
            mCloseBtn: setIsOpenPanel,
            travelType: TravelType,
        });
        (async () => {
            const res = await fetch(
                `${domainbyEnv()}/_webassets/customsearchpaneldata/trs1newlightspeed.js`
            );
            const dataString = await res.text();
            const data = (() => {
                const newVariable = dataString
                    .replace(/\r?\n|\r/g, '')
                    .replace(/(?:var|let|const)\s(\w+)\s=/g, '"$1":')
                    .replace(/;/g, ',')
                    .replace(/,$/g, '')
                    .replace(/'/g, '"');
                return JSON.parse('{' + newVariable + '}');
            })();

            if (Keywords) {
                setDefaultArrive(Keywords);
            } else if (ArriveID) {
                const ArriveArray = ArriveID?.split(',');
                ArriveArray?.pop();

                const ArriveString = ArriveArray?.reduce((accu: string[], arriveItem: string) => {
                    const tempItem = arriveItem.replace(/-/g, ',_');
                    const [vLinewebarea, vLinetravel, vLine] = tempItem.split(',');

                    let text: string;
                    if (vLinetravel === '_') {
                        text = data.vLine[vLine];
                    } else if (vLinewebarea === '') {
                        text = data.vLinetravel[vLine][`${vLinetravel}${vLine}`];
                    } else {
                        text = data.vLinewebarea[`${vLinetravel}${vLine}`][`_${vLinewebarea}`];
                    }

                    return [...accu, text];
                }, []);

                setDefaultArrive(ArriveString?.join('、')!);
            }
        })();
    }, []);

    const moduleName = 'searchPanel_block_m';

    return (
        <>
            <div className={moduleName}>
                <span
                    className={cN(`${moduleName}_input`, { hasValue: defaultArrive })}
                    onClick={() => setIsOpenPanel(true)}
                >
                    {defaultArrive ? defaultArrive : '搜尋行程'}
                </span>
                <button className={`${moduleName}_button`} onClick={() => setIsOpenPanel(true)}>
                    <Icons.Magnifier fill="#fff" />
                </button>
                <SlidePage
                    visible={isOpenPanel}
                    setIsVisible={setIsOpenPanel}
                    title="團體旅遊"
                    isHeaderShow={false}
                >
                    <div id="SearchPanelM" />
                </SlidePage>
            </div>
            {isScroll && (
                <div
                    id="fixedTopElement"
                    className={cN(moduleName, 'fixed', isOpenPanel ? 'openSearch' : '')}
                >
                    <span
                        className={cN(`${moduleName}_input`)}
                        onClick={() => setIsOpenPanel(true)}
                    >
                        <Icons.Magnifier fill="#f03742" />
                        <span>搜尋</span>
                    </span>
                    <SlidePage
                        visible={isOpenPanel}
                        setIsVisible={setIsOpenPanel}
                        title="團體旅遊"
                        isHeaderShow={false}
                    >
                        <div id="SearchPanelM" />
                    </SlidePage>
                </div>
            )}
        </>
    );
};

const switchByOptions = [
    {
        text: '切換行程顯示',
        value: 'List',
    },
    {
        text: '切換個團顯示',
        value: 'Cards',
    },
];

const sortTypeOptions = {
    // 關鍵字搜尋的結果才可以使用關聯度排序
    flatBtns: Keywords
        ? [
              {
                  text: '依關聯度',
                  value: -1, // 送出時送 null
              },
              {
                  text: '依出發日',
                  value: 3,
              },
          ]
        : [
              {
                  text: '依出發日',
                  value: 3,
              },
          ],
    dropdowns: [
        [
            {
                text: '依售價',
                value: 0,
            },
            {
                text: '依售價低到高',
                value: 1,
            },
            {
                text: '依售價高到低',
                value: 2,
            },
        ],
        [
            {
                text: '依天數',
                value: 0,
            },
            {
                text: '依天數低到高',
                value: 4,
            },
            {
                text: '依天數高到低',
                value: 5,
            },
        ],
    ],
};

const Search = () => {
    const moduleClass = 'search';
    const [isFilterMOpen, setIsFilterMOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    const GroupListData = useSelector(
        (store: RootState) => store.search.GroupListInfo.data || store.search.GroupListInfoM.data
    );
    const trigger: string = useSelector((store: RootState) => store.search.Trigger);

    useEffect(() => {
        dispatch(setPageType('search'));
    }, [dispatch]);

    const isprod = process.env.ENV === 'prod';

    const countryLink = () => {
        const taiwan = {
            href: `${domainbyEnv(false)}/category/taiwan/`,
            text: '台灣團體',
        };

        const foreign = {
            href: `${domainbyEnv(false)}/category/`,
            text: '國外團體',
        };
        const groupfree = {
            href: `//${isprod ? '' : 'u'}vacation.liontravel.com/category/zh-tw/index`,
            text: '團體自由行',
        };

        if (TravelType === '2') return [groupfree];

        if (Keywords || GroupID) return [taiwan, { ...foreign, spliter: '/' }];

        if (ArriveID && typeof ArriveID === 'string') {
            const ArriveIDArray = ArriveID?.split(',');
            const ArriveIDArrayLength =
                ArriveIDArray[ArriveIDArray.length - 1] !== ''
                    ? ArriveIDArray.length
                    : ArriveIDArray.length - 1;

            const taiwanAry = ArriveIDArray.filter(function (item) {
                return item.match(/-9$/g);
            });

            if (taiwanAry.length == ArriveIDArrayLength) {
                // 全部目的地都在台灣時
                return [taiwan];
            } else if (taiwanAry.length == 0) {
                // 沒目的地在台灣時
                return [foreign];
            } else {
                return [taiwan, { ...foreign, spliter: '/' }];
            }
        }

        return [taiwan, { ...foreign, spliter: '/' }];
    };

    const homeDomain = (() => {
        switch (process.env.ENV) {
            case 'prod':
                return '//www.liontravel.com';
            default:
                return '//uwww.liontravel.com';
        }
    })();

    const routes = [
        {
            href: homeDomain,
            text: '雄獅首頁',
        },
        ...countryLink(),
        {
            href: undefined,
            text: '搜尋結果頁',
        },
    ];

    const calculateDiscount = (orig: string, sales: string) => {
        if (!orig) {
            return 0;
        }
        const origNum = parseInt(orig.replace(/,/g, ''));
        const salesNum = parseInt(sales.replace(/,/g, ''));
        return ((origNum - salesNum) / origNum) * 100;
    };

    //寫入ga4
    useEffect(() => {
        const ga4Arr: any = [];
        const getBreadCrumb = routes
            .flatMap((route) => {
                return route.text;
            })
            .join();
        if (GroupListData) {
            GroupListData.NormGroupList.forEach((groupItem: I_NormGroup, ind: number) => {
                const ga4Data = {
                    item_id: groupItem.GroupList[0].GroupID,
                    item_list_id: groupItem.NormGroupID.toUpperCase(),
                    item_name: groupItem.TourName,
                    affiliation: groupItem.TourSource === 'GoUni' ? 'Uplan Travel' : 'Lion Travel',
                    currency: 'TWD',
                    discount: groupItem.GroupIsVip
                        ? calculateDiscount(
                              groupItem.GroupVipOrigPrice,
                              groupItem.GroupStraightPrice
                          ).toFixed(2)
                        : 0,
                    index: ind + 1 + (GroupListData.CurrentPage - 1) * 10,
                    item_brand: 'Lion Travel',
                    item_category: getBreadCrumb.includes('國外') ? 'B2CWebpd' : 'B2CWebtwpd',
                    price: Number(`${groupItem.GroupStraightPrice}`.split(',').join('')),
                };
                ga4Arr.push(ga4Data);
            });
            // 查看trigger裡面是否有這個Page
            const gate = trigger.includes(`,${GroupListData.CurrentPage}`);
            // 只有當頁面改變才會重新傳一筆ga4，並且記錄過去進入過的頁面
            if (!gate) {
                window.dataLayer = window.dataLayer || [];
                ga4Arr.length !== 0 &&
                    window.dataLayer.push({
                        event: 'view_item_list',
                        ecommerce: {
                            items: ga4Arr,
                        },
                    });
                const newTrigger = trigger + ',' + GroupListData.CurrentPage;
                dispatch(setTrigger(newTrigger));
            }

            // 當傳過來沒有資料還是得推一個ga4
            if (GroupListData.NormGroupList.length === 0 && trigger === '') {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'view_item_list',
                    ecommerce: {
                        items: [],
                    },
                });
                dispatch(setTrigger(','));
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GroupListData, trigger]);

    return (
        <div className={moduleClass}>
            <SearchPanelBlock pageType="search" routes={routes} travelType={TravelType} />

            <div className={`${moduleClass}_content`}>
                <BreadCrumb routes={routes} />
                <h3
                    className={`${moduleClass}_title ${
                        TravelType === '2' ? `groupfree_title` : ''
                    }`}
                />
                <SearchPanelBlockM />
                <TagFilters onConfirm={(finalItems: I_FilterItem[]) => {}} />
                <div className={`${moduleClass}_resultWrap`}>
                    <div className={`${moduleClass}_filtersWrap`}>
                        <Filters
                            isSlideOpen={isFilterMOpen}
                            setSlideOpen={setIsFilterMOpen}
                            onConfirm={(finalItems: I_FilterItem[]) => {}}
                        />
                    </div>
                    <div className={`${moduleClass}_result`}>
                        <div className={`${moduleClass}_conditions`}>
                            <Conditions />
                            <Sorters
                                onClickFlatBtnM={() => setIsFilterMOpen(true)}
                                switchByOptions={switchByOptions}
                                sortTypeOptions={sortTypeOptions}
                            />
                        </div>
                        <ResultCards />
                    </div>
                </div>
            </div>
        </div>
    );
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <Search />
        </Provider>
    </React.StrictMode>
);

export default Search;
